import type { DelegatedSendUndoHandle } from './agent';
import type { LocalCodingAgentRuntime } from './agentDelegation';
import type { MessagingPlatform } from './messagingPlatform.enum';
import type { PersonIdentityPayload } from './personDirectory';

/** A conversation created and driven by the DoBox executor rather than the
 *  free-form /assistant. One conversation per (userId, entryId); every run
 *  on it is executor-mode. */
export enum AgentConversationKind {
  DOBOX_EXECUTOR = 'DOBOX_EXECUTOR',
}

export enum InboxEntryLinkKind {
  CHAT_LINK = 'CHAT_LINK',
  DELEGATION = 'DELEGATION',
  EXECUTOR = 'EXECUTOR',
}

/** How a run came to exist. Carries the structural authority an AUTO tier
 *  needs: only TYPED and ANSWER (plus a typed-instruction DELEGATED) can ever
 *  qualify — SUGGESTION, a chip click, never does, no matter what the model
 *  later decides to do with it. */
export enum ExecutorTrigger {
  TYPED = 'TYPED',
  SUGGESTION = 'SUGGESTION',
  PLAN_CLICK = 'PLAN_CLICK',
  APPROVAL = 'APPROVAL',
  ANSWER = 'ANSWER',
  PICKER = 'PICKER',
  RETRY = 'RETRY',
  DELEGATED = 'DELEGATED',
  RESUME = 'RESUME',
  CONTINUATION = 'CONTINUATION',
  STANDING_PERMISSION = 'STANDING_PERMISSION',
  NEXT_STEP = 'NEXT_STEP',
  /** AS-05: the owner trialing a DRAFT custom agent on the item it was
   *  created from — carries structural authority the same way TYPED/ANSWER
   *  do (the owner's own explicit click), scoped additionally to a DRAFT
   *  template, which no other trigger is ever allowed to run. */
  AGENT_TRIAL = 'AGENT_TRIAL',
  /** A watched reply (or the deadline of a wait) re-entering the delegation. Never auto-eligible:
   *  the reply is third-party text, so it carries no authority of its own. */
  REPLY = 'REPLY',
}

export enum ExecutorAttemptKind {
  FIRST = 'FIRST',
  AGAIN = 'AGAIN',
  RETRY = 'RETRY',
}

/** PLAN_CLICK covers approving a plan item's rendered effect too — there is
 *  deliberately no separate PLAN_APPROVAL member to avoid two directive kinds
 *  meaning the same thing. */
export enum ExecutorDirectiveKind {
  APPROVAL = 'APPROVAL',
  PLAN_CLICK = 'PLAN_CLICK',
  ANSWER = 'ANSWER',
  PICKER = 'PICKER',
  RETRY = 'RETRY',
  RESUME = 'RESUME',
  CONTINUATION = 'CONTINUATION',
  NEXT_STEP = 'NEXT_STEP',
  REPLY = 'REPLY',
}

export enum ConsentSource {
  CLICK = 'CLICK',
  TYPED = 'TYPED',
}

/** The resolved, per-request mode a run actually executes under — distinct
 *  from DoBoxExecutorConfigMode (runtimeFlags.ts), which is the raw stored
 *  config and additionally has ALLOWLIST (a membership test that resolves
 *  down to ON or OFF for a given user before it ever reaches a run). */
export enum ExecutorMode {
  OFF = 'OFF',
  SHADOW = 'SHADOW',
  ON = 'ON',
}

export enum ExecutorLedgerKind {
  PROPOSAL = 'PROPOSAL',
  RECEIPT = 'RECEIPT',
  QUESTION = 'QUESTION',
  CAPABILITY_GAP = 'CAPABILITY_GAP',
  PREPARED = 'PREPARED',
  BATCH_APPLY_DECISION = 'BATCH_APPLY_DECISION',
  MEMORY_OFFER = 'MEMORY_OFFER',
  PERMISSION_OFFER = 'PERMISSION_OFFER',
  /** An agent asked someone outside the workspace and is waiting for the answer. */
  REPLY_WAIT = 'REPLY_WAIT',
}

export enum ExecutorLedgerStatus {
  PREPARED = 'PREPARED',
  PENDING = 'PENDING',
  EXECUTING = 'EXECUTING',
  EXECUTED = 'EXECUTED',
  FAILED = 'FAILED',
  SUPERSEDED = 'SUPERSEDED',
  EXPIRED = 'EXPIRED',
  STALE = 'STALE',
  UNDONE = 'UNDONE',
  DISMISSED = 'DISMISSED',
  DECLINED = 'DECLINED',
  OPEN = 'OPEN',
  ANSWERED = 'ANSWERED',
  RESUMING = 'RESUMING',
  SATISFIED = 'SATISFIED',
  CANCELLED = 'CANCELLED',
}

export enum ActionFamily {
  SOURCE_REPLY = 'SOURCE_REPLY',
  OUTBOUND_MESSAGE = 'OUTBOUND_MESSAGE',
  CALENDAR_EVENT = 'CALENDAR_EVENT',
  DOBOX_INTERNAL = 'DOBOX_INTERNAL',
  KNOWLEDGE_WRITE = 'KNOWLEDGE_WRITE',
  FLOW = 'FLOW',
  AGENT = 'AGENT',
  MCP_READ = 'MCP_READ',
  MCP_WRITE = 'MCP_WRITE',
  MEMORY = 'MEMORY',
  CODE_DELEGATION = 'CODE_DELEGATION',
  CAPABILITY = 'CAPABILITY',
}

/** AUTO_WITH_UNDO and AUTO_WITH_RETRACT are deliberately separate tiers, not
 *  one "reversible" tier: UNDO means the effect can be taken back before the
 *  counterpart ever saw it (a scheduled-send cancel, a reversible internal
 *  write); RETRACT means the content was already seen and the reversal is a
 *  retraction (Slack chat.delete after the push, a calendar cancellation sent
 *  after the invite) — a materially weaker guarantee the gate and the card
 *  copy must keep distinguishing. */
export enum ActionTier {
  READ = 'READ',
  AUTO = 'AUTO',
  AUTO_WITH_UNDO = 'AUTO_WITH_UNDO',
  AUTO_WITH_RETRACT = 'AUTO_WITH_RETRACT',
  APPROVE_ONCE = 'APPROVE_ONCE',
  ALWAYS_APPROVE = 'ALWAYS_APPROVE',
  UNAVAILABLE = 'UNAVAILABLE',
}

export enum CriticalPredicate {
  UNTRUSTED_RECIPIENT = 'UNTRUSTED_RECIPIENT',
  NEW_EXTERNAL_DOMAIN = 'NEW_EXTERNAL_DOMAIN',
  PUBLIC_OR_SHARED_CHANNEL = 'PUBLIC_OR_SHARED_CHANNEL',
  EXT_SHARED_UNKNOWN = 'EXT_SHARED_UNKNOWN',
  ATTACHMENT = 'ATTACHMENT',
  TOO_MANY_RECIPIENTS = 'TOO_MANY_RECIPIENTS',
  TOO_MANY_ITEMS = 'TOO_MANY_ITEMS',
  DESTRUCTIVE = 'DESTRUCTIVE',
  MONEY_OR_LEGAL = 'MONEY_OR_LEGAL',
  FIRST_CONTACT = 'FIRST_CONTACT',
  OTHER_MEMBERS_DOBOX = 'OTHER_MEMBERS_DOBOX',
  PERMANENT_AUTOMATION = 'PERMANENT_AUTOMATION',
  OUT_OF_THREAD_FACT = 'OUT_OF_THREAD_FACT',
  DOBOX_TRIGGER_MATCH = 'DOBOX_TRIGGER_MATCH',
  HEADERS_UNVERIFIED = 'HEADERS_UNVERIFIED',
  REPLY_TO_EXTERNAL_DOMAIN = 'REPLY_TO_EXTERNAL_DOMAIN',
  LIST_WITHOUT_SEND_AS = 'LIST_WITHOUT_SEND_AS',
  BCC_ONLY = 'BCC_ONLY',
}

export enum ReversalKind {
  NONE = 'NONE',
  SCHEDULED_SEND_CANCEL = 'SCHEDULED_SEND_CANCEL',
  SLACK_DELETE = 'SLACK_DELETE',
  CALENDAR_CANCEL = 'CALENDAR_CANCEL',
  INTERNAL_INVERSE = 'INTERNAL_INVERSE',
}

export enum Provenance {
  USER_SUPPLIED = 'USER_SUPPLIED',
  USER_SUPPLIED_PRIOR = 'USER_SUPPLIED_PRIOR',
  SOURCE_GROUNDED = 'SOURCE_GROUNDED',
  UNGROUNDED = 'UNGROUNDED',
}

export enum RecipientTrust {
  TRUSTED = 'TRUSTED',
  NEW_RECIPIENT = 'NEW_RECIPIENT',
  EXTERNAL_DOMAIN = 'EXTERNAL_DOMAIN',
  SELF_EXCLUDED = 'SELF_EXCLUDED',
  LIST = 'LIST',
}

export enum ProvenanceNote {
  ONLY_IN_COUNTERPART_MESSAGE = 'ONLY_IN_COUNTERPART_MESSAGE',
  FROM_YOUR_BRAIN = 'FROM_YOUR_BRAIN',
  FROM_DECISION = 'FROM_DECISION',
  FROM_YOUR_SENT_MESSAGE = 'FROM_YOUR_SENT_MESSAGE',
}

export enum ExecutorAttentionReason {
  SEND_FAILED = 'SEND_FAILED',
  NEEDS_APPROVAL = 'NEEDS_APPROVAL',
  NEEDS_INPUT = 'NEEDS_INPUT',
  NEEDS_CONNECTION = 'NEEDS_CONNECTION',
  READY_TO_RESOLVE = 'READY_TO_RESOLVE',
  SUGGESTED_DECISION = 'SUGGESTED_DECISION',
}

/** Mirrors every ExecutorAttentionReason member plus NONE — the rendered,
 *  single-value attention state a card/list row carries, where
 *  ExecutorAttentionReason is the (possibly several) raw reasons that fed it.
 *  Keep the two enums' shared members in sync; deriveExecutorAttention below
 *  is what is allowed to go from one to the other. */
export enum ExecutorAttention {
  NONE = 'NONE',
  SEND_FAILED = 'SEND_FAILED',
  NEEDS_APPROVAL = 'NEEDS_APPROVAL',
  NEEDS_INPUT = 'NEEDS_INPUT',
  NEEDS_CONNECTION = 'NEEDS_CONNECTION',
  READY_TO_RESOLVE = 'READY_TO_RESOLVE',
  SUGGESTED_DECISION = 'SUGGESTED_DECISION',
}

export enum AskUserOrigin {
  FLOW = 'FLOW',
  DOBOX_EXECUTOR = 'DOBOX_EXECUTOR',
}

export enum ExecutorToolStatus {
  EXECUTED = 'EXECUTED',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  ALREADY_PENDING = 'ALREADY_PENDING',
  ALREADY_EXECUTED = 'ALREADY_EXECUTED',
  IN_FLIGHT = 'IN_FLIGHT',
  NEEDS_TARGET = 'NEEDS_TARGET',
  STALE_ENVELOPE = 'STALE_ENVELOPE',
  UNAVAILABLE = 'UNAVAILABLE',
  FAILED = 'FAILED',
  ASKED = 'ASKED',
  ALREADY_ASKED = 'ALREADY_ASKED',
  SATISFIED = 'SATISFIED',
  UNVERIFIED_NEED = 'UNVERIFIED_NEED',
  DECLINED = 'DECLINED',
}

export enum ExecutorEffectKind {
  REPLY = 'REPLY',
  COMPOSE = 'COMPOSE',
  CALENDAR_EVENT = 'CALENDAR_EVENT',
  DOBOX_WRITE = 'DOBOX_WRITE',
  MCP_CALL = 'MCP_CALL',
  BRAIN_WRITE = 'BRAIN_WRITE',
  FLOW_RUN = 'FLOW_RUN',
  MEMORY = 'MEMORY',
  CODE = 'CODE',
}

export enum CodeDelegationStage {
  NEEDS_CHOICE = 'NEEDS_CHOICE',
  PLANNING = 'PLANNING',
  PLAN_READY = 'PLAN_READY',
  IMPLEMENTING = 'IMPLEMENTING',
  PR_OPEN = 'PR_OPEN',
  MERGED = 'MERGED',
  FAILED = 'FAILED',
  QUEUED_OFFLINE = 'QUEUED_OFFLINE',
  CANCELLED = 'CANCELLED',
}

export enum DelegationBlockedReason {
  NEEDS_APPROVAL = 'NEEDS_APPROVAL',
  NEEDS_INPUT = 'NEEDS_INPUT',
  NEEDS_CONNECTION = 'NEEDS_CONNECTION',
  SEND_FAILED = 'SEND_FAILED',
  /** The agent asked someone and waits for the answer; the owner has nothing to do. */
  WAITING_REPLY = 'WAITING_REPLY',
}

export enum PullRequestState {
  DRAFT = 'DRAFT',
  OPEN = 'OPEN',
  MERGED = 'MERGED',
  CLOSED = 'CLOSED',
}

export enum PullRequestChecksStatus {
  NONE = 'NONE',
  PENDING = 'PENDING',
  PASSING = 'PASSING',
  FAILING = 'FAILING',
}

export enum PullRequestReviewDecision {
  NONE = 'NONE',
  APPROVED = 'APPROVED',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  REVIEW_REQUIRED = 'REVIEW_REQUIRED',
}

/** Counts and enum states only; never a check name, a reviewer or comment text. */
export interface PullRequestSignals {
  prChecksStatus?: PullRequestChecksStatus;
  prChecksFailedCount?: number;
  prReviewDecision?: PullRequestReviewDecision;
  prReviewCommentCount?: number;
}

export enum DoBoxInternalUndoOp {
  COMMENT_ADDED = 'COMMENT_ADDED',
  CHECKLIST_ADDED = 'CHECKLIST_ADDED',
  SUBTASK_ADDED = 'SUBTASK_ADDED',
  SUBTASK_TOGGLED = 'SUBTASK_TOGGLED',
  ENTRY_UPDATED = 'ENTRY_UPDATED',
  MOVED = 'MOVED',
  SUGGESTION_ACCEPTED = 'SUGGESTION_ACCEPTED',
  RESOLVED = 'RESOLVED',
  COLLECTIONS_UPDATED = 'COLLECTIONS_UPDATED',
}

export enum DoBoxPlanItemKind {
  REPLY = 'REPLY',
  COMPOSE = 'COMPOSE',
  SCHEDULE = 'SCHEDULE',
  INTERNAL_UPDATE = 'INTERNAL_UPDATE',
  CAPABILITY = 'CAPABILITY',
  DELEGATE_CODE = 'DELEGATE_CODE',
  ASK_SOMEONE = 'ASK_SOMEONE',
  MARK_DONE = 'MARK_DONE',
  AUTOMATE_RECURRING = 'AUTOMATE_RECURRING',
  ROUTE_MATCH = 'ROUTE_MATCH',
  /** No agent today is capable of the blocked action — server-rendered
   *  "No agent yet" / "Set up an agent" entry point, not tool-executed. */
  AGENT_SETUP = 'AGENT_SETUP',
  /** A user or chat request names an existing agent (own or team) able to
   *  do the work — "delegate this to {agent}". */
  DELEGATE_AGENT = 'DELEGATE_AGENT',
}

export enum DoBoxPlanSlotType {
  TEXT = 'TEXT',
  DATE = 'DATE',
  DATE_TIME = 'DATE_TIME',
  PERSON = 'PERSON',
  EMAIL_ADDRESS = 'EMAIL_ADDRESS',
  CHOICE = 'CHOICE',
}

export enum DoBoxPlanItemAvailability {
  AVAILABLE = 'AVAILABLE',
  NEEDS_CAPABILITY = 'NEEDS_CAPABILITY',
  UNAVAILABLE = 'UNAVAILABLE',
}

/** EXECUTE for READ, AUTO* and APPROVE_ONCE items whose full effect is
 *  server-rendered (the click IS the approval); REVIEW for ALWAYS_APPROVE
 *  (opens the full card for an explicit confirm); UNAVAILABLE otherwise. */
export enum DoBoxPlanCtaKind {
  EXECUTE = 'EXECUTE',
  REVIEW = 'REVIEW',
  UNAVAILABLE = 'UNAVAILABLE',
}

/** Status of an executor action mutation result ({run, alreadyExecuted,
 *  status}) from an API pre-check against the ledger pointer — a fast,
 *  optimistic read. The final outcome always renders from the ledger rows
 *  themselves, never from this value alone. */
export enum ExecutorActionResultStatus {
  QUEUED = 'QUEUED',
  ALREADY_EXECUTED = 'ALREADY_EXECUTED',
  STALE = 'STALE',
  REJECTED = 'REJECTED',
}

export enum AgentSubmitOrigin {
  COMPOSER = 'COMPOSER',
  SUGGESTION_CHIP = 'SUGGESTION_CHIP',
}

export enum ProposalDecision {
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
}

export interface ExecutorItemRef {
  conversationId: string;
  runId: string;
  msgIdx: string;
}

/**
 * Structural resume/approval directive carried on a USER AgentMessage (or an
 * executor mutation) in place of free text like the legacy "Confirmed: act-1"
 * format. Every field is an id, digest, enum, boolean or number — never user
 * or model text — so the worker can act on it without a model round-trip.
 */
export interface ExecutorDirective {
  kind: ExecutorDirectiveKind;
  itemRef?: ExecutorItemRef;
  instructionConvMsgIdx?: string;
  inheritFromRunId?: string;
  planItemId?: string;
  planFingerprint?: string;
  previewDigest?: string;
  approvedActionIds?: string[];
  selectedSubItemIds?: string[];
  consentSource?: ConsentSource;
  choiceThreadKey?: string;
  choiceOptionIndex?: number;
  continuationHop?: number;
  inheritsAutoEligibility?: boolean;
  edited?: boolean;
  renderedAt?: number;
  allowDuplicate?: boolean;
  nextStepId?: string;
  /** AS-04 edit: the owner adjusted a tool's own numeric limit value (the
   *  wizard's "Add 200 credits" ceiling) before approving the card this
   *  directive resolves. Keyed by the tool's own `AgentToolPolicyPerToolLimit.param`
   *  name — the ONLY key the worker ever reads out of this map — so an edit
   *  can never touch an argument the policy did not grant a limit for. A
   *  key that does not match the tool's own limit param, or a tool with no
   *  limit at all, is ignored rather than merged (executeApprovedProposal.ts,
   *  apps/api). */
  editedToolArgs?: Record<string, number>;
  /** REPLY only: the wait group whose answers (or deadline) started this run. */
  replyWaitGroupId?: string;
  /** REPLY only: why the run started. */
  replyOutcome?: ReplyWaitOutcome;
  /** REPLY only: an email reply whose thread the provider could not confirm — the run's
   *  approval switch is withheld, so everything it leads to gets a card. */
  replyUnverified?: boolean;
}

/** Exhaustive by construction: a new ExecutorLedgerKind member with no entry
 *  here fails to compile. QUESTION and CAPABILITY_GAP use OPEN, never
 *  PENDING — "pending" implies an approval is awaiting a decision, and an
 *  open question or capability gap is waiting on information, not approval. */
export const EXECUTOR_LEDGER_STATUSES_BY_KIND: Record<
  ExecutorLedgerKind,
  readonly ExecutorLedgerStatus[]
> = {
  [ExecutorLedgerKind.PROPOSAL]: [
    ExecutorLedgerStatus.PENDING,
    ExecutorLedgerStatus.EXECUTING,
    ExecutorLedgerStatus.EXECUTED,
    ExecutorLedgerStatus.FAILED,
    ExecutorLedgerStatus.SUPERSEDED,
    ExecutorLedgerStatus.EXPIRED,
    ExecutorLedgerStatus.STALE,
    ExecutorLedgerStatus.UNDONE,
    ExecutorLedgerStatus.DECLINED,
    ExecutorLedgerStatus.DISMISSED,
  ],
  [ExecutorLedgerKind.RECEIPT]: [
    ExecutorLedgerStatus.EXECUTING,
    ExecutorLedgerStatus.EXECUTED,
    ExecutorLedgerStatus.FAILED,
    ExecutorLedgerStatus.UNDONE,
  ],
  [ExecutorLedgerKind.QUESTION]: [
    ExecutorLedgerStatus.OPEN,
    ExecutorLedgerStatus.ANSWERED,
    ExecutorLedgerStatus.RESUMING,
    ExecutorLedgerStatus.EXPIRED,
    ExecutorLedgerStatus.STALE,
    ExecutorLedgerStatus.DISMISSED,
  ],
  [ExecutorLedgerKind.CAPABILITY_GAP]: [
    ExecutorLedgerStatus.OPEN,
    ExecutorLedgerStatus.SATISFIED,
    ExecutorLedgerStatus.DISMISSED,
    ExecutorLedgerStatus.EXPIRED,
  ],
  [ExecutorLedgerKind.PREPARED]: [
    ExecutorLedgerStatus.PREPARED,
    ExecutorLedgerStatus.STALE,
    ExecutorLedgerStatus.EXPIRED,
    ExecutorLedgerStatus.EXECUTED,
    ExecutorLedgerStatus.FAILED,
    ExecutorLedgerStatus.UNDONE,
    ExecutorLedgerStatus.SUPERSEDED,
  ],
  [ExecutorLedgerKind.BATCH_APPLY_DECISION]: [
    ExecutorLedgerStatus.PENDING,
    ExecutorLedgerStatus.EXECUTING,
    ExecutorLedgerStatus.EXECUTED,
    ExecutorLedgerStatus.FAILED,
    ExecutorLedgerStatus.DECLINED,
  ],
  [ExecutorLedgerKind.MEMORY_OFFER]: [
    ExecutorLedgerStatus.OPEN,
    ExecutorLedgerStatus.ANSWERED,
    ExecutorLedgerStatus.DISMISSED,
    ExecutorLedgerStatus.EXPIRED,
  ],
  [ExecutorLedgerKind.PERMISSION_OFFER]: [
    ExecutorLedgerStatus.OPEN,
    ExecutorLedgerStatus.ANSWERED,
    ExecutorLedgerStatus.DISMISSED,
    ExecutorLedgerStatus.EXPIRED,
  ],
  [ExecutorLedgerKind.REPLY_WAIT]: [
    ExecutorLedgerStatus.OPEN,
    ExecutorLedgerStatus.ANSWERED,
    ExecutorLedgerStatus.EXPIRED,
    ExecutorLedgerStatus.CANCELLED,
  ],
};

export const EXECUTOR_ATTENTION_PRECEDENCE: readonly ExecutorAttentionReason[] =
  [
    ExecutorAttentionReason.SEND_FAILED,
    ExecutorAttentionReason.NEEDS_APPROVAL,
    ExecutorAttentionReason.NEEDS_INPUT,
    ExecutorAttentionReason.NEEDS_CONNECTION,
    ExecutorAttentionReason.READY_TO_RESOLVE,
    ExecutorAttentionReason.SUGGESTED_DECISION,
  ];

/** Exhaustive by construction: a new ExecutorAttentionReason member with no
 *  entry here fails to compile, keeping it in lock-step with ExecutorAttention. */
const ATTENTION_BY_REASON: Record<ExecutorAttentionReason, ExecutorAttention> =
  {
    [ExecutorAttentionReason.SEND_FAILED]: ExecutorAttention.SEND_FAILED,
    [ExecutorAttentionReason.NEEDS_APPROVAL]: ExecutorAttention.NEEDS_APPROVAL,
    [ExecutorAttentionReason.NEEDS_INPUT]: ExecutorAttention.NEEDS_INPUT,
    [ExecutorAttentionReason.NEEDS_CONNECTION]:
      ExecutorAttention.NEEDS_CONNECTION,
    [ExecutorAttentionReason.READY_TO_RESOLVE]:
      ExecutorAttention.READY_TO_RESOLVE,
    [ExecutorAttentionReason.SUGGESTED_DECISION]:
      ExecutorAttention.SUGGESTED_DECISION,
  };

/** Collapses a set of raw attention reasons (an InboxEntry/DoBox can have
 *  several at once) into the single value a list row or card shows, by
 *  EXECUTOR_ATTENTION_PRECEDENCE. Accepts a Set or a plain array; unknown
 *  strings are ignored rather than thrown on, since this reads a persisted
 *  value that may predate a since-removed reason. */
export function deriveExecutorAttention(
  reasons: Iterable<string> | null | undefined,
): ExecutorAttention {
  if (!reasons) {
    return ExecutorAttention.NONE;
  }
  const reasonSet = new Set<string>(reasons);
  for (const reason of EXECUTOR_ATTENTION_PRECEDENCE) {
    if (reasonSet.has(reason)) {
      return ATTENTION_BY_REASON[reason];
    }
  }
  return ExecutorAttention.NONE;
}

export type ExecutorRecipientRole = 'TO' | 'CC';

export interface ExecutorPreviewRecipient {
  display: string;
  trust: RecipientTrust;
  role: ExecutorRecipientRole;
}

export interface ExecutorTextRange {
  start: number;
  end: number;
}

export interface ExecutorProvenanceEntry {
  note: ProvenanceNote;
  params?: Record<string, string>;
}

export interface ExecutorPreviewField {
  label: string;
  value: string;
}

/** Label of the MCP_CALL preview row whose value is the COUNT (a decimal string) of tool arguments left out of the card. */
export const MCP_CALL_OMITTED_ARGUMENTS_FIELD_LABEL = 'Omitted details';

export interface ExecutorMemoryPreview {
  quote: string;
  origin: string;
}

export interface ExecutorCodePreview {
  deviceLabel: string;
  runtime: LocalCodingAgentRuntime;
  repositorySlug: string;
}

/**
 * Server-rendered preview of what an action will actually do, discriminated
 * by `kind`. The model never writes this directly — it is built from the
 * tool's resolved input the same way AgentDelegationActionEffect is, so a
 * card can never show a label the model invented instead of the real effect.
 */
export interface ExecutorEffectPreview {
  kind: ExecutorEffectKind;
  platform?: MessagingPlatform;
  accountLabel?: string;
  destinationLabel?: string;
  threadLabel?: string;
  recipients?: ExecutorPreviewRecipient[];
  subject?: string;
  bodyText?: string;
  attachments?: string[];
  unsupportedClaims?: ExecutorTextRange[];
  criticalMarkers?: CriticalPredicate[];
  provenance?: ExecutorProvenanceEntry[];
  headersVerified?: boolean;
  fields?: ExecutorPreviewField[];
  isCustomServer?: boolean;
  memory?: ExecutorMemoryPreview;
  code?: ExecutorCodePreview;
}

export type ExecutorNextStepKind = 'MARK_DONE' | 'REMIND_IF_NO_REPLY';
export type ExecutorNextStepStatus = 'OFFERED' | 'DONE' | 'DISMISSED';

export interface ExecutorNextStep {
  nextStepId: string;
  kind: ExecutorNextStepKind;
  status: ExecutorNextStepStatus;
}

export type ExecutorSendState = 'SCHEDULED' | 'SENT' | 'DONE' | 'FAILED';
export type ExecutorUndoKind =
  | 'UNDO'
  | 'CANCEL_SCHEDULED'
  | 'RETRACT'
  | 'INVERSE'
  | 'NONE';
export type ExecutorApprovedVia =
  | 'CLICK'
  | 'TYPED_CONSENT'
  | 'PLAN_CLICK'
  | 'STANDING_PERMISSION'
  | 'AUTO'
  /** Ran without a card only because the owner's AUTONOMOUS mode allows it (SKIP alone would not have). */
  | 'AUTONOMOUS';

export interface ExecutorMemoryRef {
  decisionId: string;
  decidedAt: number;
}

export interface ExecutorReceipt {
  executedAt: number;
  sendState: ExecutorSendState;
  scheduledFor?: number;
  undoKind: ExecutorUndoKind;
  undoDeadline?: number;
  undo?: DelegatedSendUndoHandle;
  nextSteps?: ExecutorNextStep[];
  memoryRefs?: ExecutorMemoryRef[];
  approvedVia?: ExecutorApprovedVia;
}

export const PERSON_IDENTITY_QUESTION_KIND = 'PERSON_IDENTITY' as const;

export type ExecutorQuestionKind =
  | 'GENERAL'
  | 'TARGET_THREAD'
  | 'RECIPIENT_NOT_ALLOWED'
  | typeof PERSON_IDENTITY_QUESTION_KIND;

/** Why a tool refused a request in a way only the owner can resolve. A stable code the web acts on. */
export enum ExecutorRefusalReason {
  /** An email recipient the agent may not write to: not saved on the agent, not a task participant. */
  RECIPIENT_NOT_ALLOWED = 'recipient_not_allowed',
}

/** Carried inside the sealed content of the question row it belongs to; never a readable attribute. */
export interface ExecutorRefusal {
  reason: ExecutorRefusalReason;
  /** The address the agent wanted to write to. */
  address?: string;
}

export interface ExecutorQuestionOption {
  optionIndex: number;
  label: string;
}

export interface ExecutorQuestionThread {
  threadKey: string;
  platform: MessagingPlatform;
  label: string;
  counterpartLabel: string;
  lastMessageAt: number;
}

export interface ExecutorQuestion {
  question: string;
  options: ExecutorQuestionOption[];
  missingItems?: string[];
  questionKind: ExecutorQuestionKind;
  threads?: ExecutorQuestionThread[];
  /** Set when the question exists because a tool refused something the owner can allow. */
  refusal?: ExecutorRefusal;
  /** PERSON_IDENTITY only: what the server binds when the owner picks an option. */
  identity?: PersonIdentityPayload;
}

/** capabilityKey, blockedAction and remedy hold libs/constants enum values —
 *  models never import libs/constants, so these stay typed as string here. */
export interface ExecutorCapabilityGap {
  capabilityKey: string;
  blockedAction: string;
  remedy: string;
  connectionId?: string;
  toolName?: string;
  resourceLabel?: string;
  accountMismatch?: boolean;
  alternatives: string[];
  isSatisfiedNow?: boolean;
  othersWaitingCount?: number;
}

export interface ExecutorCodeDelegationOption {
  optionId: string;
  label: string;
}

export interface ExecutorCodeDelegation {
  stage: CodeDelegationStage;
  deviceId: string;
  deviceLabel: string;
  runtime: LocalCodingAgentRuntime;
  repositorySlug: string;
  machineState?: string;
  activeJobCount?: number;
  isExternalOrigin: boolean;
  prUrl?: string;
  prNumber?: number;
  queuedUntil?: number;
  queuedOnDeviceCount?: number;
  options?: ExecutorCodeDelegationOption[];
}

export type ExecutorBatchItemVerdict = 'FULL' | 'PARTIAL';

export interface ExecutorBatchItem {
  subItemId: string;
  entryId: string;
  taskGroupId: string;
  entrySubject: string;
  platform: MessagingPlatform;
  verdict: ExecutorBatchItemVerdict;
  effect: ExecutorEffectPreview;
  status: ExecutorToolStatus;
}

export interface ExecutorLedgerDescriptor {
  ledgerKind: ExecutorLedgerKind;
  proposalStatus: ExecutorLedgerStatus;
  family: ActionFamily;
  destKeyHmac?: string;
  fingerprintHmac?: string;
  expiresAt?: number;
  instructionConvMsgIdx?: string;
  tier?: ActionTier;
}

/** scope, actionClass and channel hold libs/constants enum values — models
 *  never import libs/constants, so these stay typed as string here. */
export interface ExecutorMemoryOffer {
  quote: string;
  scope: string;
  counterpartLabel?: string;
}

export interface ExecutorPermissionOffer {
  offerId: string;
  actionClass: string;
  channel: string;
  counterpartLabel?: string;
  approvals: number;
}

export interface ExecutorBatch {
  items: ExecutorBatchItem[];
}

/**
 * The single contract between the worker writer (persistMessage.ts), the API
 * readers (undo, answer, plan mutations) and the web parser. This is the
 * shape of the `output` of the (separately defined, apps/api-local)
 * ToolResultPayload persisted in AgentMessage.content for an executor tool
 * call — never the whole AgentMessage row.
 */
export interface ExecutorToolOutput {
  schemaVersion: number;
  status: ExecutorToolStatus;
  tier?: ActionTier;
  reasons?: CriticalPredicate[];
  preview?: ExecutorEffectPreview;
  receipt?: ExecutorReceipt;
  question?: ExecutorQuestion;
  capabilityGap?: ExecutorCapabilityGap;
  codeDelegation?: ExecutorCodeDelegation;
  batch?: ExecutorBatch;
  memoryOffer?: ExecutorMemoryOffer;
  permissionOffer?: ExecutorPermissionOffer;
  errorCode?: string;
  alternatives?: string[];
  /** A refusal's explanation, ready to say to the owner in their own language. No tool or parameter names. */
  ownerMessage?: string;
  ledger?: ExecutorLedgerDescriptor;
  /** `delegate_to_agent`'s own executed output (AS-07) — the resolved
   *  target, so a web reader gets a typed field instead of casting through
   *  the raw tool result. Absent for every other tool. */
  delegated?: { agentName: string; agentTemplateId: string };
  /** `compose_outbound` armed a reply wait: the model reads that the run ends here. (The REPLY_WAIT
   *  row's own body keeps its sealed state under `output.replyWait`, a different shape.) */
  replyWaitArmed?: ExecutorReplyWaitReceipt;
  /** EXECUTED because the owner's SKIP or AUTONOMOUS switch lifted the card, not because a person approved it
   *  or the tool's own tier ran it: the run may go on to the remaining steps of the job. */
  ownerSwitchApplied?: boolean;
}

export const EXECUTOR_LEDGER_SCHEMA_VERSION = 1;

export interface ProposalDecisionRecord {
  proposalConvMsgIdx: string;
  decision: ProposalDecision;
  confirmedActionIds: string[];
  skippedActionIds: string[];
}

/** Why a REPLY run started: the wait's rule was met, its deadline passed first, or a reminder is due. */
export enum ReplyWaitOutcome {
  RULE_MET = 'RULE_MET',
  TIMED_OUT = 'TIMED_OUT',
  /** Half the wait has passed with no answer: the run proposes the one reminder, nothing more. */
  REMINDER_DUE = 'REMINDER_DUE',
}

/** What the model reads right after `compose_outbound` armed a wait. No identity, no id. */
export interface ExecutorReplyWaitReceipt {
  /** Epoch ms the wait ends. */
  expiresAt: number;
  recipientCount: number;
}
